'use client'

/**
 * CurrencyConverter — a live converter driven by the SSR-passed ECB reference rates. Defaults to
 * EUR → USD (the top search). Converts client-side; no round-trips.
 */

import { useMemo, useState } from 'react'
import { ArrowLeftRight } from 'lucide-react'
import type { FxRates } from '@/lib/dashboard/fxRates'

export default function CurrencyConverter({ data }: { data: FxRates }) {
  // Build a USD-anchored rate table: units of X per 1 USD. USD itself = 1.
  const perUsd = useMemo(() => {
    const m: Record<string, number> = { USD: 1 }
    for (const p of data.pairs) m[p.code] = p.perUsd
    return m
  }, [data.pairs])
  const names = useMemo(() => {
    const m: Record<string, string> = { USD: 'US Dollar' }
    for (const p of data.pairs) m[p.code] = p.name
    return m
  }, [data.pairs])
  const codes = useMemo(() => ['USD', ...data.pairs.map((p) => p.code)], [data.pairs])

  const [amount, setAmount] = useState('1')
  const [from, setFrom] = useState('EUR')
  const [to, setTo] = useState('USD')

  const amt = Number(amount.replace(/,/g, '')) || 0
  // convert: amount in `from` → USD → `to`
  const result = perUsd[from] && perUsd[to] ? (amt / perUsd[from]) * perUsd[to] : null
  const unitRate = perUsd[from] && perUsd[to] ? (1 / perUsd[from]) * perUsd[to] : null

  const swap = () => { setFrom(to); setTo(from) }

  return (
    <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-4">
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Amount</span>
          <input
            inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm font-bold tabular-nums text-white outline-none focus:border-amber-500/60"
          />
          <select value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1.5 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-[13px] text-zinc-200 outline-none focus:border-amber-500/60">
            {codes.map((c) => <option key={c} value={c}>{c} - {names[c]}</option>)}
          </select>
        </label>
        <button onClick={swap} aria-label="Swap currencies" className="mb-9 rounded-lg border border-zinc-700 bg-zinc-800/60 p-2 text-zinc-300 transition-colors hover:border-amber-500/50 hover:text-amber-300">
          <ArrowLeftRight className="h-4 w-4" />
        </button>
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Converts to</span>
          <div className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm font-bold tabular-nums text-emerald-300">
            {result != null ? result.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '—'}
          </div>
          <select value={to} onChange={(e) => setTo(e.target.value)} className="mt-1.5 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-[13px] text-zinc-200 outline-none focus:border-amber-500/60">
            {codes.map((c) => <option key={c} value={c}>{c} - {names[c]}</option>)}
          </select>
        </label>
      </div>
      {unitRate != null && (
        <p className="mt-3 text-[12px] text-zinc-500">
          1 {from} = <span className="font-semibold text-zinc-300">{unitRate.toLocaleString('en-US', { maximumFractionDigits: 4 })} {to}</span>
          {data.date && <span className="text-zinc-600"> · ECB reference rate, {data.date}</span>}
        </p>
      )}
    </div>
  )
}
